// Animated Speaker Avatar for Remotion Podcast Composition
// Shows speaker photo with glow effect when active, falls back to initials

import React from "react";
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  Img,
} from "remotion";

interface SpeakerAvatarProps {
  name: string;
  hostIndex: number;
  isActive: boolean;
  color: {
    primary: string;
    glow: string;
    bg: string;
  };
  avatarUrl?: string;
}

export const SpeakerAvatar: React.FC<SpeakerAvatarProps> = ({
  name,
  hostIndex,
  isActive,
  color,
  avatarUrl,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const time = frame / fps;

  // Entrance animation
  const entrance = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 60 },
    delay: hostIndex * 10,
  });

  // Pulse when active (breathing effect)
  const pulse = isActive ? 1 + Math.sin(time * 4) * 0.04 : 1;

  // Glow intensity oscillation
  const glowIntensity = isActive
    ? 0.4 + Math.sin(time * 3) * 0.2
    : 0.05;

  // Scale transition between active/inactive
  const scale = isActive ? 1.08 : 0.92;
  const opacity = isActive ? 1 : 0.5;

  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarSize = 140;
  const ringSize = avatarSize + 16;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
        transform: `scale(${entrance * scale * pulse})`,
        opacity: entrance * opacity,
        transition: "transform 0.3s ease, opacity 0.3s ease",
      }}
    >
      {/* Outer glow ring */}
      <div
        style={{
          position: "relative",
          width: ringSize,
          height: ringSize,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Animated glow */}
        <div
          style={{
            position: "absolute",
            inset: -12,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color.glow}${Math.round(glowIntensity * 255)
              .toString(16)
              .padStart(2, "0")} 0%, transparent 70%)`,
          }}
        />

        {/* Ring border */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `3px solid ${isActive ? color.primary : "rgba(255,255,255,0.15)"}`,
            boxShadow: isActive
              ? `0 0 20px ${color.glow}40, inset 0 0 20px ${color.glow}10`
              : "none",
            transition: "border-color 0.3s ease, box-shadow 0.3s ease",
          }}
        />

        {/* Avatar circle — image or initials fallback */}
        <div
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: "50%",
            overflow: "hidden",
            background: `linear-gradient(135deg, ${color.bg}, ${color.primary}40)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            filter: isActive ? "none" : "saturate(0.5) brightness(0.8)",
            transition: "filter 0.3s ease",
          }}
        >
          {avatarUrl ? (
            <Img
              src={avatarUrl}
              style={{
                width: avatarSize,
                height: avatarSize,
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 48,
                fontWeight: 700,
                color: isActive ? color.glow : "rgba(255,255,255,0.6)",
                letterSpacing: 2,
                transition: "color 0.3s ease",
              }}
            >
              {initials}
            </span>
          )}
        </div>

        {/* Active speaking indicator dot */}
        {isActive && (
          <div
            style={{
              position: "absolute",
              bottom: 4,
              right: 4,
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: color.primary,
              border: "2.5px solid rgba(0,0,0,0.3)",
              boxShadow: `0 0 12px ${color.glow}80`,
            }}
          />
        )}
      </div>

      {/* Name label */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <span
          style={{
            color: isActive ? "white" : "rgba(255,255,255,0.5)",
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: 0.5,
            transition: "color 0.3s ease",
          }}
        >
          {name}
        </span>
        {isActive && (
          <span
            style={{
              color: color.glow,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            Speaking
          </span>
        )}
      </div>
    </div>
  );
};

export default SpeakerAvatar;
